import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { select } from '@angular-redux/store';
import { TranslateService } from '@ngx-translate/core';

import {
    CustomerFacade,
    MediumFacade,
    NotificationFacade,
    ProductsSelectorFacade,
    RidePlannerFacade,
    ShoppingCartFacade,
    ShoppingCartState
} from '@ridango/components';
import { BaseComponent, CoreFacade, Language, Product } from '@ridango/core';

import { combineLatest } from 'rxjs';
import { Observable } from 'rxjs-compat/Observable';
import { takeUntil, take } from 'rxjs/operators';
import Env from '@ridango/core/env';

@Component({
    templateUrl: './book-ride-specify-page.component.html',
    styleUrls: ['./book-ride-specify-page.component.scss']
})
export class BookRideSpecifyPageComponent extends BaseComponent implements OnInit, OnDestroy {
    passenger$: Observable<number> = this.ridePlanner.passenger$;
    selectedMedium$: Observable<any> = this.medium.medium$;
    loading$: Observable<boolean> = this.ridePlanner.loading$;
    error$: Observable<HttpErrorResponse> = this.ridePlanner.error$;
    passengerPermission$: Observable<boolean> = this.customerFacade.passengerPermission$;
    products$: Observable<Product[]> = this.ridePlanner.products$;
    price$: Observable<number> = this.ridePlanner.estimatedPrice$;
    ticketPayloadsPosted$: Observable<boolean> = this.shoppingCart.ticketPayloadsPosted$;
    cart$: Observable<ShoppingCartState> = this.shoppingCart.cart$;
    selectedLanguage$: Observable<Language> = this.coreFacade.selectedLanguage$;

    isFlowValidated: boolean;
    stops: any;
    priorityTime: any;
    passenger: number;
    productId: string;
    additionalInformation: string;
    passengerPermission: boolean;
    bookingFlow = {};
    products;
    selectedProducts;
    selectedMedium;
    profileId;
    selectedLanguageCode: string;

    public get showAdditionalComponent(): boolean {
        return this.env.CREATE_ORDER_SHOW_ADDITIONAL != null ? this.env.CREATE_ORDER_SHOW_ADDITIONAL : true;
    }

    @select(['application', 'assetsPath']) assetsPath$;

    constructor(
        @Inject('ENV') private env: Env,
        private ridePlanner: RidePlannerFacade,
        private medium: MediumFacade,
        private customerFacade: CustomerFacade,
        private router: Router,
        private translate: TranslateService,
        private notification: NotificationFacade,
        private productsSelector: ProductsSelectorFacade,
        private shoppingCart: ShoppingCartFacade,
        private coreFacade: CoreFacade
    ) {
        super();
    }

    ngOnInit() {
        this.ridePlanner.step$.pipe(takeUntil(this.componentDestroyed$)).subscribe((step) => {
            if (step === 3) {
                if (this.env.ONDEMAND?.PAYMENT_ON_WEB) {
                    this.ticketPayloadsPosted$
                        .pipe(takeUntil(this.componentDestroyed$))
                        .subscribe((isPayloadsPosted) => {
                            if (isPayloadsPosted) {
                                this.cart$.pipe(take(1)).subscribe((c) => {
                                    this.shoppingCart.updateCart(c.shopping_cart_id);
                                    this.router.navigate([this.translate.currentLang, 'book-a-ride', 'review']);
                                });
                            }
                        });
                } else {
                    this.router.navigate([this.translate.currentLang, 'book-a-ride', 'review']);
                }
            } else if (step > 2) {
                this.ridePlanner.setStep(2);
            } else if (step !== 2) {
                this.router.navigate([this.translate.currentLang, 'book-a-ride']);
            }
        });

        this.ridePlanner.resetSpecification();
        this.ridePlanner.fetchProducts();

        this.customerFacade.getCustomerProfile();

        this.medium.setCardTypeMask(this.env.ONDEMAND?.CARD_TYPE_MASK);

        this.passengerPermission$.pipe(takeUntil(this.componentDestroyed$)).subscribe((permission) => {
            this.passengerPermission = permission;
        });

        this.selectedLanguage$.pipe(takeUntil(this.componentDestroyed$)).subscribe((l) => {
            this.selectedLanguageCode = l?.code;
        });

        this.products$.pipe(takeUntil(this.componentDestroyed$)).subscribe((products) => {
            this.products = products;
            this.productsSelector.selected$
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((selected: Map<number, number>) => {
                    this.selectedProducts = selected;
                    const price = this.calculatePrice(selected);
                    this.ridePlanner.setEstimatedPrice(price);
                });
        });

        combineLatest([this.passenger$, this.price$, this.selectedMedium$])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(([passenger, price, medium]) => {
                this.passenger = passenger;
                this.selectedMedium = medium;
                this.isFlowValidated =
                    passenger &&
                    (price || !this.env.ONDEMAND?.PAYMENT_ON_WEB) &&
                    (medium.relatedCardId || medium.cardId);
            });

        this.ridePlanner.stops$.pipe(takeUntil(this.componentDestroyed$)).subscribe((stops) => (this.stops = stops));
    }

    submitBooking() {
        this.ridePlanner.priorityTime$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((time) => (this.priorityTime = time));

        this.ridePlanner.additionalInformation$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((info) => (this.additionalInformation = info));

        this.ridePlanner.error$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((error) => error && this.handleError(error));

        this.customerFacade.profile$.pipe(take(1)).subscribe((profile) => (this.profileId = profile.id));

        const additionalProductsAry = Object.entries(this.selectedProducts).map(([k, v]) => {
            return {
                product_id: +k,
                customer_id: this.profileId ? this.profileId : null,
                amount: v
            };
        });

        this.bookingFlow = {
            priority_time: this.priorityTime,
            attendant_count: this.passenger,
            stops: this.stops,
            web_client_comment: this.additionalInformation,
            additionalProducts: additionalProductsAry
        };

        this.ridePlanner.postBookingFlow(this.bookingFlow);

        if (this.env.ONDEMAND?.PAYMENT_ON_WEB) {
            const tickets = this.makeShoppingCart();
            this.shoppingCart.postPayloads({ payloads: tickets, language: this.selectedLanguageCode });
        }
    }

    handleError(e: HttpErrorResponse) {
        if (e.error.error) {
            this.notification.error(this.translate.instant(e.error.error));
        } else if (e.url.includes('auth/refresh')) {
            this.notification.error(this.translate.instant('UNAUTHORIZED'));
        } else {
            this.notification.error(this.translate.instant(e.error));
        }
        window.scroll(0, 0);
    }

    private calculatePrice(selected: Map<number, number>): number {
        let price = 0;
        this.passenger = 0;
        this.productId = '';
        for (const productId in selected) {
            const product = this.products.find((p) => p.product_id.toString() == productId);
            if (product) {
                if (!this.productId) {
                    this.productId = productId;
                }
                const productCount = selected[productId];
                price += product.price * productCount;
                this.passenger += productCount;
            }
        }
        this.ridePlanner.setPassenger(this.passenger);

        return price;
    }

    private makeShoppingCart() {
        const tickets = [];
        for (const productId in this.selectedProducts) {
            for (let i = 0; i < this.selectedProducts[productId]; i++) {
                const ticket = {
                    card_type: this.selectedMedium.cardId ? null : 'T',
                    channel: 'web',
                    product_id: productId,
                    region_id: this.env.REGION_ID,
                    shopping_cart_id: null,
                    sms: 'N'
                };
                if (this.selectedMedium.relatedCardId) {
                    ticket['related_card_id'] = this.selectedMedium.relatedCardId;
                } else {
                    ticket['card_id'] = this.selectedMedium.cardId;
                }
                tickets.push(ticket);
            }
        }
        return tickets;
    }
}
